/**
 * Ardiye hesaplayicisinin PDF'i: sitedeki gorunumun aynisi (ustte TBH logosu, solda form, sagda
 * mavi sonuc paneli), A4 dikey, dogrudan indirilir. Hesaplayici bolumu html2canvas ile masaustu
 * genisliginde (PDF_RENDER_WIDTH) cizilir; kullanici telefondan indirse de iki kolon yerlesimi
 * korunur. Cizim, sayfanin kopyasinda yapilir: giris kutulari girilen degeri gosteren duz metne
 * (tarih GG.AA.YYYY) cevrilir, dugmeler ve kutu ikonlari cikarilir. Sayfadaki asil hesaplayici
 * degismez. Kutuphaneler yalnizca dugmeye basilinca yuklenir (ilk sayfa yukunu buyutmez).
 */
export const PDF_RENDER_WIDTH = 1440;

const A4 = { width: 210, height: 297, margin: 10 };

export function formatPdfDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
}

export function storagePdfFileName(startDate: string, endDate: string): string {
  const safe = (value: string) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : 'tarih');
  return `TBH-Port-Adana-Ardiye-Hesaplama-${safe(startDate)}_${safe(endDate)}.pdf`;
}

/**
 * Kopya bolumu PDF gorunumune getirir. `source` sayfadaki asil bolumdur; kutularin guncel degeri
 * oradan okunur (kopyalanan input'un value ozelligi her zaman tasinmaz).
 */
export function preparePdfClone(clone: HTMLElement, source: HTMLElement, logoSrc: string, logoAlt: string): void {
  const doc = clone.ownerDocument;
  clone.classList.add('storage-pdf-export');

  const sourceInputs = Array.from(source.querySelectorAll('input'));
  Array.from(clone.querySelectorAll('input')).forEach((input, index) => {
    const value = sourceInputs[index]?.value ?? input.value;
    const text = doc.createElement('span');
    text.className = 'storage-pdf-value';
    text.textContent = input.type === 'date' ? formatPdfDate(value) : value;
    input.replaceWith(text);
  });

  clone
    .querySelectorAll('.calculator-submit, .calculator-actions, .calculator-date-icon, .calculator-quantity-icon')
    .forEach((node) => node.remove());

  const container = clone.querySelector('.container') || clone;
  const header = doc.createElement('div');
  header.className = 'storage-pdf-header';
  const logo = doc.createElement('img');
  logo.src = logoSrc;
  logo.alt = logoAlt;
  header.appendChild(logo);
  container.insertBefore(header, container.firstChild);
}

/** Gorseli A4 sayfasina kenar boslugunu koruyarak, oranini bozmadan yerlestirir (mm) */
export function fitToA4(pixelWidth: number, pixelHeight: number) {
  const maxWidth = A4.width - A4.margin * 2;
  const maxHeight = A4.height - A4.margin * 2;
  let width = maxWidth;
  let height = (pixelHeight * width) / pixelWidth;
  if (height > maxHeight) {
    height = maxHeight;
    width = (pixelWidth * height) / pixelHeight;
  }
  return { x: (A4.width - width) / 2, y: A4.margin, width, height };
}

/**
 * Logo SVG'si yalnizca viewBox tasir (width/height yok); html2canvas boyutsuz SVG'yi yanlis olcekler
 * (logo yerine buyutulmus bir parca cizilir). Logo once tarayicida bilinen boyutta PNG'ye cevrilir.
 */
export async function rasterizeLogo(src: string, height = 192): Promise<string> {
  const text = await (await fetch(src)).text();
  const viewBox = /viewBox="\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)\s*"/.exec(text);
  const ratio = viewBox ? Number(viewBox[1]) / Number(viewBox[2]) : 662.73 / 119.15;
  const width = Math.round(height * ratio);
  const sized = /<svg[^>]*\swidth=/.test(text) ? text : text.replace('<svg', `<svg width="${width}" height="${height}"`);
  const url = URL.createObjectURL(new Blob([sized], { type: 'image/svg+xml' }));
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * html2canvas yazi taban cizgisini sayfaya ekledigi 1x1 GIF ile olcer (FontMetrics). Sitenin genel
 * `img { display: block }` kurali bu gorseli satirdan kopardigi icin taban cizgisi fazla olculur ve
 * tum yazilar ~8px asagi kayar. Olcum gorseli PDF uretilirken satir ici tutulur.
 */
const HTML2CANVAS_METRIC_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function installFontMetricFix(doc: Document): () => void {
  const style = doc.createElement('style');
  style.setAttribute('data-storage-pdf', '');
  style.textContent = `img[src="${HTML2CANVAS_METRIC_IMAGE}"] { display: inline !important; max-width: none !important; }`;
  doc.head.appendChild(style);
  return () => style.remove();
}

export async function downloadStoragePdf(
  section: HTMLElement,
  options: { logoSrc: string; logoAlt: string; fileName: string; title: string },
): Promise<void> {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const logo = await rasterizeLogo(options.logoSrc).catch(() => options.logoSrc);
  const removeFix = installFontMetricFix(section.ownerDocument);
  let canvas: HTMLCanvasElement;
  try {
    canvas = await html2canvas(section, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      windowWidth: PDF_RENDER_WIDTH,
      onclone: (_doc, cloned) => preparePdfClone(cloned, section, logo, options.logoAlt),
    });
  } finally {
    removeFix();
  }
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const box = fitToA4(canvas.width, canvas.height);
  pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', box.x, box.y, box.width, box.height);
  pdf.setProperties({ title: options.title, creator: 'TBH Port Adana' });
  pdf.save(options.fileName);
}
